/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * FooterDetilsCard
 *
 * @file  FooterDetilsCard.jsx
 * @author Anuja Gaikwad
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import mixins from '../../assets/styles/mixins.scss';
import SafeButtonWrapper from './SafeButtonWrapper';

const StyledPaper = styled('div')({
  '&&': {
    padding: '32px 48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 24,
    width: '100%',
    maxWidth: 'min(var(--disclaimer-max-width, 800px), calc(100vw - 32px))',
    borderRadius: 24,
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.1)',
    flexShrink: 0,
    boxSizing: 'border-box',

    '@media (max-width: 767px)': {
      padding: '24px 24px'
    }
  }
});

const StyledCloseButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    right: '16px',
    top: '16px',
    color: mixins.blackColor,
    fontSize: 18,
    width: 18,
    height: 18
  }
});

const StyledTitle = styled(DialogTitle)({
  '&&': {
    padding: 0
  }
});

const StyledTitleText = styled(Typography)({
  '&&': {
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    letterSpacing: '-0.4px',
    fontStyle: 'normal',
    color: mixins.color_text_primary,
    lineHeight: '30px',
    fontFamily: mixins.fontFamilyPlusSans
  }
});

const StyledContent = styled(DialogContent)({
  '&&': {
    padding: 0,
    marginBottom: 0,
    gap: 18,
    display: 'flex',
    flexDirection: 'column'
  }
});

const StyledContentText = styled(Typography)({
  '&&': {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '28px',
    color: mixins.zambezi,
    letterSpacing: '-0.4px',
    fontFamily: mixins.fontFamilyPlusSans
  }
});

const StyledActions = styled(DialogActions)({
  '&&': {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
    margin: 0
  }
});

const StyledConfirmButton = styled(Button)({
  '&&': {
    padding: 8,
    borderRadius: 1000,
    backgroundColor: mixins.amethyst,
    color: mixins.whiteFont,
    minWidth: 140,
    minHeight: 24,
    '&:hover': {
      backgroundColor: `${mixins.amethyst} !important`
    }
  }
});

const StyledConfirmButtonText = styled(Typography)({
  '&&': {
    color: mixins.chalkWhiteFont,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    letterSpacing: '-0.4px',
    textTransform: 'none'
  }
});

const FooterDetilsCard = ({
  open,
  onClose,
  title,
  content,
  confirmText,
  onConfirm,
  showCloseButton,
  className,
  titleClassName,
  contentClassName,
  actionsClassName,
  classes,
  isLoading,
  customStyles
}) => {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (onClose) {
      onClose();
    }
  };

  const renderContent = () => {
    if (typeof content === 'string') {
      return (
        <StyledContentText className={classes?.contentText || ''} variant="body1">
          {content}
        </StyledContentText>
      );
    }
    if (Array.isArray(content)) {
      return content.map((text, index) => (
        <StyledContentText key={index} className={classes?.contentText || ''} variant="body1">
          {text}
        </StyledContentText>
      ));
    }

    return content;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className={className}
      PaperProps={{
        component: StyledPaper,
        className: classes?.paper || ''
      }}
      style={customStyles}
    >
      {showCloseButton && onClose && (
        <StyledCloseButton
          className={classes?.closeButton || ''}
          onClick={onClose}
        >
          <CloseIcon />
        </StyledCloseButton>
      )}
      <StyledTitle className={`${classes?.title || ''} ${titleClassName || ''}`.trim()}>
        <StyledTitleText className={classes?.titleText || ''} variant="h2">
          {title}
        </StyledTitleText>
      </StyledTitle>
      <StyledContent className={`${classes?.content || ''} ${contentClassName || ''}`.trim()}>
        {renderContent()}
      </StyledContent>
      <StyledActions className={`${classes?.actions || ''} ${actionsClassName || ''}`.trim()}>
        <SafeButtonWrapper isLoading={isLoading}>
          <StyledConfirmButton
            className={classes?.confirmButton || ''}
            onClick={handleConfirm}
            variant="contained"
          >
            <StyledConfirmButtonText className={classes?.confirmButtonText || ''} variant="button">
              {confirmText}
            </StyledConfirmButtonText>
          </StyledConfirmButton>
        </SafeButtonWrapper>
      </StyledActions>
    </Dialog>
  );
};

FooterDetilsCard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.node
  ]).isRequired,
  confirmText: PropTypes.string,
  onConfirm: PropTypes.func,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  actionsClassName: PropTypes.string,
  classes: PropTypes.object,
  isLoading: PropTypes.bool,
  customStyles: PropTypes.object
};

FooterDetilsCard.defaultProps = {
  onClose: null,
  confirmText: 'Confirm',
  onConfirm: null,
  showCloseButton: true,
  className: '',
  titleClassName: '',
  contentClassName: '',
  actionsClassName: '',
  classes: {},
  isLoading: false,
  customStyles: {}
};

const FooterDetilsCardWithStyles = (props) => <FooterDetilsCard {...props} />;

export default FooterDetilsCardWithStyles;
